import { type FormEvent, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/useAuth";
import { ApiError, apiCheckUsername } from "../auth/api";
import { toast } from "../lib/toast";
import type { AuthMode } from "../auth/context";

const USERNAME_RE = /^[A-Za-z0-9_-]{3,12}$/;

// Sign-in / create modal — React port of openAuth() from account.js, reusing the
// same .auth-* classes so the copied CSS applies, with focus trap + inert backdrop.
export function AuthModal({ mode: initialMode, onClose }: { mode: AuthMode; onClose: () => void }) {
  const { register, signIn } = useAuth();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  const isCreate = mode === "create";

  // Take the page out of the a11y tree + tab order, trap focus, Esc to close.
  useEffect(() => {
    const sheets = [...document.querySelectorAll<HTMLElement>("body > .sheet")];
    for (const n of sheets) {
      n.inert = true;
      n.setAttribute("aria-hidden", "true");
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !cardRef.current) return;
      const list = [...cardRef.current.querySelectorAll<HTMLElement>("button, input, a[href]")].filter(
        (n) => !(n as HTMLButtonElement).disabled && n.offsetParent !== null,
      );
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      for (const n of sheets) {
        n.inert = false;
        n.removeAttribute("aria-hidden");
      }
    };
  }, [onClose]);

  useEffect(() => {
    usernameRef.current?.focus();
  }, [mode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const u = username.trim();
    if (!USERNAME_RE.test(u)) {
      setError("Username must be 3–12 characters — letters, numbers, _ or -.");
      usernameRef.current?.focus();
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      if (isCreate) {
        const avail = await apiCheckUsername(u);
        if (avail.valid && avail.available === false) {
          setError("That username is already taken.");
          return;
        }
        await register({ username: u, password, email: email.trim() || undefined });
        toast(`Account created — welcome, ${u}.`);
      } else {
        await signIn({ username: u, password });
        toast(`Signed in as ${u}.`);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="auth-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="auth-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        aria-describedby="auth-sub"
        ref={cardRef}
      >
        <button className="auth-close" type="button" aria-label="Close" onClick={onClose}>
          ✕
        </button>
        <form className="auth-form" noValidate onSubmit={submit}>
          <h2 className="auth-title" id="auth-title">
            {isCreate ? "Create your reArcade account" : "Sign in to reArcade"}
          </h2>
          <p className="auth-sub" id="auth-sub">
            {isCreate
              ? "One account plays every multiplayer game and saves your wins."
              : "Welcome back. Your account carries across every multiplayer game."}
          </p>
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          <label className="auth-field">
            <span className="auth-label">Username</span>
            <input
              className="auth-input"
              ref={usernameRef}
              type="text"
              name="username"
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={12}
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            {isCreate && <p className="auth-hint">3–12 characters — letters, numbers, _ or -</p>}
          </label>
          <label className="auth-field">
            <span className="auth-label">Password</span>
            <input
              className="auth-input"
              type="password"
              name="password"
              autoComplete={isCreate ? "new-password" : "current-password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {isCreate && (
            <label className="auth-field auth-field-email">
              <span className="auth-label">
                Email <span className="auth-optional">optional</span>
              </span>
              <input
                className="auth-input"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="auth-hint">Optional — stored for future account recovery.</p>
            </label>
          )}
          <button className={`auth-submit${busy ? " is-busy" : ""}`} type="submit" disabled={busy}>
            {busy ? (isCreate ? "Creating…" : "Signing in…") : isCreate ? "Create account" : "Sign in"}
          </button>
          <p className="auth-switch">
            <span className="auth-toggle-text">{isCreate ? "Already have an account?" : "New to reArcade?"}</span>{" "}
            <button
              className="auth-toggle"
              type="button"
              onClick={() => {
                setMode(isCreate ? "signin" : "create");
                setError("");
              }}
            >
              {isCreate ? "Sign in" : "Create one"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
